import React, { ReactNode, createContext, useContext, useRef, useCallback, useMemo } from 'react';
import { Node } from '@react-types/shared';
import { mergeProps, useFocusVisible, useHover, useListBoxSection, useOption } from 'react-aria';
import { ListState } from 'react-stately';
import { isMobile } from '@direct-frontend/platform-code';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';

import { TestProps } from '../../../internal/types/testTypes';
import { SectionContent } from '../SectionContent/SectionContent';
import { IconSelection } from '../Icon/Icon';
import { ListBoxContext } from '../../ListBox/utils';
import { cnListBoxItemSeparator, isBorderElement, useItemClasses } from '../utils';
import { Separator } from '../Separator/Separator';
import { ListSection } from '../../List/ListSection';
import { useItemTooltip } from '../../../hooks/useItemTooltip';

import { Item as ItemType, Option as OptionType, Section as SectionType } from './types';
import { getItemKey, isOption, isSection } from './utils';

//<editor-fold desc="Item">
interface ItemProps<ValueType extends string> extends Loggable {
    node: Node<ItemType<ValueType>>;
    state: ListState<ItemType<ValueType>>;
    baseTestId?: string;
}

export const ListAddonContext = createContext<{ listAddon?: ReactNode }>({});

export const Item = <ValueType extends string>(props: ItemProps<ValueType>) => {
    const { node, state, baseTestId = 'Item', logNode } = props;
    const item = node.value;
    const { listAddon } = useContext(ListAddonContext);
    const nextKey = state.collection.getKeyAfter('addon');
    const isSectionNext = nextKey && state.collection.getItem(nextKey)?.type === 'section';

    if (!item) {
        return null;
    }

    if (isSection(item)) {
        if (item.key === 'addonSection') {
            return (
                <>
                    <ListSection>{listAddon}</ListSection>
                    {state.collection.getLastKey() !== 'addon' && !isSectionNext && <ListSection />}
                </>
            );
        }

        return (
            <Section item={item} state={state} childNodes={node.childNodes} baseTestId={baseTestId} logNode={logNode}>
                {node.rendered}
            </Section>
        );
    }

    if (isOption(item)) {
        return (
            <Option
                item={item}
                state={state}
                data-testid={`${baseTestId}.${item.value}`}
                logNode={
                    logNode
                        ? `${typeof logNode === 'string' ? logNode : (logNode.name ?? '')}.${item.value}`
                        : undefined
                }
            >
                {node.rendered}
            </Option>
        );
    }

    throw new Error('Не поддерживаемый элемент');
};
//</editor-fold>

//<editor-fold desc="Option">
interface OptionProps<ValueType extends string, SectionType extends string> extends TestProps {
    item: OptionType<ValueType>;
    state: ListState<ItemType<ValueType, SectionType>>;
    children: ReactNode;
}

export const Option = <ValueType extends string, TSectionType extends string>(
    props: OptionProps<ValueType, TSectionType> & Loggable,
) => {
    const { item, state, children, logNode } = props;
    const listState = React.useContext(ListBoxContext) || state;
    const { addonRight, value, url } = item;
    const hasAddonRight = Boolean(addonRight);
    const ref = useRef<HTMLAnchorElement>(null);

    const { optionProps, isSelected, isDisabled, isFocused, isPressed } = useOption(
        {
            key: value,
            shouldFocusOnHover: false,
        },
        listState,
        ref,
    );

    const { isHovered, hoverProps } = useHover({ isDisabled });

    const { onPointerDown, onMouseDown, ...restProps } = optionProps;
    const { isFirst, isLast } = isBorderElement<
        ValueType,
        TSectionType,
        OptionType<ValueType>,
        SectionType<TSectionType, OptionType<ValueType>>
    >(item, state.collection, getItemKey);
    const { isFocusVisible } = useFocusVisible();
    const { className } = useItemClasses({
        isFocused: (isFocusVisible && isFocused) || isHovered,
        isDisabled,
        isPressed,
        hasAddonRight,
        isFirst,
        isLast,
    });
    const { LogProvider } = useLogger('Option', logNode);
    const withSeparator = isMobile() && value !== state.collection.getLastKey() && !isLast;
    const ItemTooltip = item.tooltip;

    const {
        triggerProps: tooltipTriggerProps,
        tooltipProps,
        descriptionNode,
    } = useItemTooltip({
        triggerRef: ref,
        hasTooltip: Boolean(ItemTooltip),
        isDisabled,
        description: item.tooltipDescription,
    });

    const handlePointerDown = useCallback<Exclude<typeof onPointerDown, undefined>>(
        e => {
            // Необходимо для того, чтобы не вызывался обработчик из react-aria,
            // который триггерит возврат фокуса в activeElement при клике по disabled элементу
            if (isDisabled) {
                return;
            }

            onPointerDown?.(e);
        },
        [onPointerDown, isDisabled],
    );

    const handleMouseDown = useCallback<Exclude<typeof onMouseDown, undefined>>(
        e => {
            // Необходимо для того, чтобы не вызывался обработчик из react-aria,
            // который триггерит возврат фокуса в activeElement при клике по disabled элементу
            if (isDisabled) {
                return;
            }

            onMouseDown?.(e);
        },
        [onMouseDown, isDisabled],
    );

    const handleProps = useMemo(
        () => ({
            onPointerDown: handlePointerDown,
            onMouseDown: handleMouseDown,
        }),
        [handlePointerDown, handleMouseDown],
    );
    const Tag = url && !isDisabled ? 'a' : 'span';

    return (
        <LogProvider>
            <Tag
                href={url}
                ref={ref}
                className={className}
                data-testid={props['data-testid']}
                {...mergeProps(restProps, handleProps, tooltipTriggerProps, hoverProps)}
            >
                {children}
                {<IconSelection isSelected={isSelected} />}
            </Tag>
            {ItemTooltip && <ItemTooltip triggerRef={ref} {...tooltipProps} />}
            {descriptionNode}
            {withSeparator && <Separator className={cnListBoxItemSeparator(null)} />}
        </LogProvider>
    );
};
//</editor-fold>

//<editor-fold desc="Section">
interface SectionProps<ValueType extends string> extends Loggable {
    item: SectionType<ValueType, ItemType<ValueType>>;
    state: ListState<ItemType<ValueType>>;
    childNodes: Iterable<Node<ItemType<ValueType>>>;
    children: ReactNode;
    baseTestId?: string;
}

const Section = <ValueType extends string>(props: SectionProps<ValueType>) => {
    const { item, state, childNodes, children, baseTestId = 'Section', logNode } = props;
    const withSeparator = item.key !== state.collection.getFirstKey();
    const { headingProps, itemProps, groupProps } = useListBoxSection({ heading: children, 'aria-label': item.title });
    const { LogProvider } = useLogger('Section', logNode);

    const options = Array.from(childNodes).map(node => (
        <Item key={node.key} node={node} state={state} baseTestId={baseTestId} logNode={logNode} />
    ));

    return (
        <LogProvider>
            <SectionContent
                title={children}
                caption={item.caption}
                headingProps={headingProps}
                itemProps={itemProps}
                groupProps={groupProps}
                withSeparator={withSeparator}
                help={item.help}
                data-testid={`${baseTestId}.${item.key}`}
                iconLeft={item.icon}
            >
                {options}
            </SectionContent>
        </LogProvider>
    );
};
//</editor-fold>
